/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  Paper,
  Grid,
  Checkbox,
  ListItemText,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { Add, Delete, Close, Search, Edit, CheckCircle } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const ScriptConfigModal = ({ open, onClose, onSave, currentScriptConfig = {}, exchange = 'NSE' }) => {  const [scriptConfigs, setScriptConfigs] = useState({});
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [selectedScriptsForBulk, setSelectedScriptsForBulk] = useState([]);  const [bulkEditValues, setBulkEditValues] = useState({
    min: '',
    perTradeLimit: '',
    max: ''
  });const [defaultValues, setDefaultValues] = useState({
    min: 0,
    perTradeLimit: 0,
    max: 0
  });
  
  const { docs } = useSelector((state) => state.trade?.instrumentData);
  const availableScripts = (docs || []).filter((doc) => doc.exchange?.name === exchange);  useEffect(() => {
    if (open) {
      // Initialize with current script config or empty object and sort alphabetically
      const currentConfig = currentScriptConfig || {};
      const sortedKeys = Object.keys(currentConfig).sort();
      const sortedConfigs = {};
      sortedKeys.forEach(key => {
        sortedConfigs[key] = currentConfig[key];
      });
      setScriptConfigs(sortedConfigs);      // Reset search and bulk edit state when modal opens
      setSearchTerm('');
      setIsBulkEditMode(false);
      setSelectedScriptsForBulk([]);
      setBulkEditValues({ min: '', perTradeLimit: '', max: '' });
      setDefaultValues({ min: 0, perTradeLimit: 0, max: 0 });
    }
  }, [open, currentScriptConfig]);  const handleAddScripts = () => {
    if (selectedScripts.length > 0) {
      const newConfigs = { ...scriptConfigs };
      selectedScripts.forEach(scriptName => {        if (!newConfigs[scriptName.toUpperCase()]) {
          newConfigs[scriptName.toUpperCase()] = {
            min: Number(defaultValues.min) || 0,
            perTradeLimit: Number(defaultValues.perTradeLimit) || 0,
            max: Number(defaultValues.max) || 0
          };
        }
      });
      
      // Sort the keys alphabetically and create a new ordered object
      const sortedKeys = Object.keys(newConfigs).sort();
      const sortedConfigs = {};
      sortedKeys.forEach(key => {
        sortedConfigs[key] = newConfigs[key];
      });
      
      setScriptConfigs(sortedConfigs);
      setSelectedScripts([]);
    }
  };
  const handleDeleteScript = (scriptName) => {
    setScriptConfigs(prev => {
      const newConfig = { ...prev };
      delete newConfig[scriptName];
      return newConfig;
    });
    // Remove from bulk selection if it was selected
    setSelectedScriptsForBulk(prev => prev.filter(name => name !== scriptName));
  };

  const handleScriptConfigChange = (scriptName, field, value) => {
    setScriptConfigs(prev => ({
      ...prev,
      [scriptName]: {
        ...prev[scriptName],
        [field]: Number(value) || 0
      }
    }));
  };
  const handleSave = () => {
    onSave(scriptConfigs);
    onClose();
  };

  // Helper function to handle default value changes
  const handleDefaultValueChange = (field, value) => {
    const numericValue = value === '' ? '' : Math.max(0, Number(value));
    setDefaultValues(prev => ({ ...prev, [field]: numericValue }));
  };

  // Search functionality
  const getFilteredScriptEntries = () => {
    const entries = Object.entries(scriptConfigs);
    if (!searchTerm) return entries;
    return entries.filter(([scriptName]) => 
      scriptName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Bulk edit functionality
  const handleToggleBulkEdit = () => {
    setIsBulkEditMode(!isBulkEditMode);
    setSelectedScriptsForBulk([]);
  };

  const handleBulkScriptSelect = (scriptName, checked) => {
    if (checked) {
      setSelectedScriptsForBulk(prev => [...prev, scriptName]);
    } else {
      setSelectedScriptsForBulk(prev => prev.filter(name => name !== scriptName));
    }
  };

  const handleSelectAllScripts = (checked) => {
    if (checked) {
      const allScriptNames = getFilteredScriptEntries().map(([scriptName]) => scriptName);
      setSelectedScriptsForBulk(allScriptNames);
    } else {
      setSelectedScriptsForBulk([]);
    }
  };

  const handleBulkEditValueChange = (field, value) => {
    setBulkEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyBulkEdit = () => {
    if (selectedScriptsForBulk.length === 0) return;
    
    setScriptConfigs(prev => {
      const newConfigs = { ...prev };
      selectedScriptsForBulk.forEach(scriptName => {
        if (newConfigs[scriptName]) {
          const updatedConfig = { ...newConfigs[scriptName] };
          if (bulkEditValues.min !== '') updatedConfig.min = Number(bulkEditValues.min) || 0;
          if (bulkEditValues.max !== '') updatedConfig.max = Number(bulkEditValues.max) || 0;
          if (bulkEditValues.perTradeLimit !== '') updatedConfig.perTradeLimit = Number(bulkEditValues.perTradeLimit) || 0;
          newConfigs[scriptName] = updatedConfig;
        }
      });
      return newConfigs;
    });
    
    // Reset bulk edit state
    setSelectedScriptsForBulk([]);
    setBulkEditValues({ min: '', perTradeLimit: '', max: '' });
  };  const getAvailableScriptsForDropdown = () => 
    availableScripts
      .filter(script => !scriptConfigs[script.name.toUpperCase()])
      .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Configure Script Settings for {exchange}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>        {/* Add New Script Section */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>          <Typography variant="subtitle1" gutterBottom>
            Add New Scripts
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={getAvailableScriptsForDropdown()}
                getOptionLabel={(option) => option.name}
                value={selectedScripts.map(scriptName => 
                  availableScripts.find(script => script.name === scriptName)
                ).filter(Boolean)}
                onChange={(event, newValue) => {
                  setSelectedScripts(newValue.map(script => script.name));
                }}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <ListItemText primary={option.name} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Scripts"
                    placeholder="Search and select scripts..."
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                      autoCorrect: 'off',
                      autoCapitalize: 'off',
                      spellCheck: 'false',
                    }}
                  />
                )}
                filterOptions={(options, { inputValue }) => 
                  options.filter(option =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.tradingsymbol?.toLowerCase().includes(inputValue.toLowerCase())
                  )
                }
                sx={{
                  '& .MuiOutlinedInput-input': {
                    cursor: 'text !important',
                  }
                }}
              />
            </Grid>
              {/* Default Values Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.neutral', 
                borderRadius: 1, 
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Set Default Values for New Scripts
                  </Typography>                  <Button 
                    size="small" 
                    onClick={() => setDefaultValues({ min: 0, perTradeLimit: 0, max: 0 })}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Reset
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      label="Min"
                      type="number"
                      value={defaultValues.min}
                      onChange={(e) => handleDefaultValueChange('min', e.target.value)}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      label="Per Trade Limit"
                      type="number"
                      value={defaultValues.perTradeLimit}
                      onChange={(e) => handleDefaultValueChange('perTradeLimit', e.target.value)}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      label="Max"
                      type="number"
                      value={defaultValues.max}
                      onChange={(e) => handleDefaultValueChange('max', e.target.value)}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid><Grid item xs={12}>              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddScripts}
                disabled={selectedScripts.length === 0}
                fullWidth
                sx={{ height: '48px' }}
              >
                Add {selectedScripts.length} Script{selectedScripts.length !== 1 ? 's' : ''} 
                {(defaultValues.min || defaultValues.perTradeLimit || defaultValues.max) && 
                  ` (Min: ${defaultValues.min}, Limit: ${defaultValues.perTradeLimit}, Max: ${defaultValues.max})`
                }
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }} />        {/* Configured Scripts Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            Configured Scripts ({Object.keys(scriptConfigs).length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleToggleBulkEdit}
            color={isBulkEditMode ? "primary" : "inherit"}
          >
            {isBulkEditMode ? 'Exit Bulk Edit' : 'Bulk Edit'}
          </Button>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search scripts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Bulk Edit Controls */}
        {isBulkEditMode && (
          <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Bulk Edit Options ({selectedScriptsForBulk.length} selected)
            </Typography>            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2.5}>
                <TextField
                  size="small"
                  label="Min"
                  type="number"
                  value={bulkEditValues.min}
                  onChange={(e) => handleBulkEditValueChange('min', e.target.value)}
                  fullWidth
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={2.5}>
                <TextField
                  size="small"
                  label="Per Trade Limit"
                  type="number"
                  value={bulkEditValues.perTradeLimit}
                  onChange={(e) => handleBulkEditValueChange('perTradeLimit', e.target.value)}
                  fullWidth
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={2.5}>
                <TextField
                  size="small"
                  label="Max"
                  type="number"
                  value={bulkEditValues.max}
                  onChange={(e) => handleBulkEditValueChange('max', e.target.value)}
                  fullWidth
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={2.5}>
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={handleApplyBulkEdit}
                  disabled={selectedScriptsForBulk.length === 0}
                  fullWidth
                  sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                >
                  Apply
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleSelectAllScripts(selectedScriptsForBulk.length !== getFilteredScriptEntries().length)}
                  sx={{ bgcolor: 'white' }}
                >
                  {selectedScriptsForBulk.length === getFilteredScriptEntries().length ? 'Deselect All' : 'Select All'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
          {getFilteredScriptEntries().length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {searchTerm ? 'No scripts found matching your search.' : 'No scripts configured yet. Add scripts using the dropdown above.'}
          </Typography>
        ) : (
          <Box>
            {/* Header Row */}
            <Grid container spacing={1} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              {isBulkEditMode && (
                <Grid item xs={0.5}>
                  <Typography variant="subtitle2" fontWeight="bold">✓</Typography>
                </Grid>
              )}
              <Grid item xs={isBulkEditMode ? 2.5 : 3}>
                <Typography variant="subtitle2" fontWeight="bold">Script Name</Typography>
              </Grid>              <Grid item xs={2.5}>
                <Typography variant="subtitle2" fontWeight="bold">Min</Typography>
              </Grid>
              <Grid item xs={2.5}>
                <Typography variant="subtitle2" fontWeight="bold">Per Trade Limit</Typography>
              </Grid>
              <Grid item xs={2.5}>
                <Typography variant="subtitle2" fontWeight="bold">Max</Typography>
              </Grid>
              <Grid item xs={1.5}>
                <Typography variant="subtitle2" fontWeight="bold">Action</Typography>
              </Grid>
            </Grid>

            {/* Script Configuration Rows */}
            {getFilteredScriptEntries().map(([scriptName, config]) => (
              <Paper key={scriptName} elevation={1} sx={{ mb: 1 }}>
                <Grid container spacing={1} sx={{ p: 1 }} alignItems="center">
                  {isBulkEditMode && (
                    <Grid item xs={0.5}>
                      <Checkbox
                        size="small"
                        checked={selectedScriptsForBulk.includes(scriptName)}
                        onChange={(e) => handleBulkScriptSelect(scriptName, e.target.checked)}
                      />
                    </Grid>
                  )}
                  <Grid item xs={isBulkEditMode ? 2.5 : 3}>
                    <Typography variant="body2" fontWeight="medium">
                      {scriptName}
                    </Typography>
                  </Grid>                  <Grid item xs={2.5}>
                    <TextField
                      type="number"
                      size="small"
                      value={config.min}
                      onChange={(e) => handleScriptConfigChange(scriptName, 'min', e.target.value)}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2.5}>
                    <TextField
                      type="number"
                      size="small"
                      value={config.perTradeLimit}
                      onChange={(e) => handleScriptConfigChange(scriptName, 'perTradeLimit', e.target.value)}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2.5}>
                    <TextField
                      type="number"
                      size="small"
                      value={config.max}
                      onChange={(e) => handleScriptConfigChange(scriptName, 'max', e.target.value)}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteScript(scriptName)}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScriptConfigModal;
